import { Button, StatusPill } from '@bb/ui';
import type { PushContentIssue, PushValidationResult } from '../../../api/client.ts';
import { issueLocation, PAYLOAD_MAX_BYTES, type FieldIssues } from './pushChecks.ts';
import { PLATFORM_LABELS, type PushPlatform } from './pushDraft.ts';

/** The same problem on both platforms reads once, without a platform. */
export function dedupeIssues(issues: readonly PushContentIssue[]): PushContentIssue[] {
  const out: PushContentIssue[] = [];
  for (const issue of issues) {
    const twin = out.findIndex((o) => o.code === issue.code && o.message === issue.message);
    if (twin < 0) out.push(issue);
    else if (out[twin]!.platform !== issue.platform) out[twin] = { ...out[twin]!, platform: undefined };
  }
  return out.map(({ platform, ...rest }) => (platform ? { ...rest, platform } : rest));
}

/** Errors of a field as one sentence for `Field`'s `error`; `undefined` when there are none. */
export const errorText = (issues: FieldIssues): string | undefined =>
  dedupeIssues(issues.errors)
    .map((i) => `${i.platform ? `${PLATFORM_LABELS[i.platform]}: ` : ''}${i.message}`)
    .join(' ') || undefined;

/** The issues of one field, under it: errors block publishing, warnings explain what a device may do. */
export function FieldIssueList({ issues }: { issues: FieldIssues }) {
  if (!issues.errors.length && !issues.warnings.length) return null;
  return (
    <>
      {dedupeIssues(issues.errors).map((i) => (
        <p key={`e/${i.code}/${i.platform ?? ''}/${i.message}`} className="bb-field__error" role="alert">
          {i.platform ? `${PLATFORM_LABELS[i.platform]}: ` : ''}
          {i.message}
        </p>
      ))}
      {dedupeIssues(issues.warnings).map((i) => (
        <p key={`w/${i.code}/${i.platform ?? ''}/${i.message}`} className="hint push-issue--warning">
          {i.platform ? `${PLATFORM_LABELS[i.platform]}: ` : ''}
          {i.message}
        </p>
      ))}
    </>
  );
}

/**
 * Every check of the message at a glance: errors and warnings with where they are, and the estimated payload size per
 * platform against the provider limit. Shows that a check is running and when it could not run.
 */
export function PushChecksSummary({ result, pending, error, onCheck, disabled }: { result: PushValidationResult | undefined; pending: boolean; error: unknown; onCheck: () => void; disabled?: boolean }) {
  const errors = result?.errors ?? [];
  const warnings = result?.warnings ?? [];
  const sizes = Object.entries(result?.estimatedPayloadBytes ?? {}) as Array<[PushPlatform, number]>;
  return (
    <section className="push-checks" aria-label="Checks" aria-busy={pending || undefined}>
      <div className="toolbar">
        {result ? (
          <span role="status">
            {errors.length ? <StatusPill tone="danger">{`${errors.length} ${errors.length === 1 ? 'error' : 'errors'}`}</StatusPill> : <StatusPill tone="success">No errors</StatusPill>}{' '}
            {warnings.length > 0 && <StatusPill tone="warning">{`${warnings.length} ${warnings.length === 1 ? 'warning' : 'warnings'}`}</StatusPill>}
          </span>
        ) : (
          <span className="hint">{disabled ? 'Not checked: the message is read-only.' : 'Not checked yet.'}</span>
        )}
        {pending && <span className="hint">Checking…</span>}
        {!disabled && (
          <Button size="sm" onClick={onCheck} disabled={pending}>
            Check now
          </Button>
        )}
      </div>
      {error ? <p role="alert">The checks could not run: {error instanceof Error ? error.message : 'unknown error'}. Edit the message or check again.</p> : null}
      {(errors.length > 0 || warnings.length > 0) && (
        <ul className="push-checks__list" aria-label="Issues">
          {errors.map((i, n) => (
            <li key={`e${n}`}>
              <StatusPill tone="danger">Error</StatusPill> {issueLocation(i)}: {i.message}
            </li>
          ))}
          {warnings.map((i, n) => (
            <li key={`w${n}`}>
              <StatusPill tone="warning">Warning</StatusPill> {issueLocation(i)}: {i.message}
            </li>
          ))}
        </ul>
      )}
      {sizes.length > 0 && (
        <p className="hint">
          Estimated payload: {sizes.map(([p, bytes]) => `${PLATFORM_LABELS[p]} ${bytes} of ${PAYLOAD_MAX_BYTES} bytes`).join(' · ')} (templates as written, longest locale).
        </p>
      )}
    </section>
  );
}
